import { useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import {
  Loader2,
  X,
  Presentation as PresentationIcon,
  CheckCircle2,
  ArrowLeft,
  ArrowRight,
  Plus,
  Trash2,
  Download,
  Image as ImageIcon,
  AlertCircle,
  FileText,
} from "lucide-react";
import clsx from "clsx";
import { api, apiUrl, authToken } from "../api";
import { Button } from "../ui";
import { useToast } from "../ui/toast";
import ResearchModePicker, {
  useResearchMode,
} from "./ResearchModePicker";

interface PlannedSlide {
  position: number;
  slide_type: "title" | "agenda" | "content" | "data" | "section" | "closing";
  title: string;
  hint: string;
  wants_image: boolean;
}

interface ProjectDoc {
  id: string;
  title: string;
  embedding_status: string;
  chunk_count: number;
  brain_store_doc_id?: number | null;
}

interface DirectionOption {
  key: string;
  label: string;
  description: string;
  use_cases: string[];
}

interface PresentationOut {
  id: string;
  project_id: string;
  owner: string;
  title: string;
  brief: string | null;
  audience: string | null;
  target_slide_count: number | null;
  language: string;
  status: string;
  plan: {
    slides: PlannedSlide[];
    deck_kind?: string | null;
    direction_classifier?: {
      direction: string;
      confidence: number | null;
      rationale: string | null;
      fallback: boolean;
    } | null;
  } | null;
  pptx_object_key: string | null;
  pdf_object_key: string | null;
  model: string | null;
  tokens_in: number;
  tokens_out: number;
  error: string | null;
  created_at: string;
  updated_at: string;
}

type Step = "brief" | "plan" | "generating" | "done";

const SLIDE_TYPES: PlannedSlide["slide_type"][] = [
  "title",
  "agenda",
  "content",
  "data",
  "section",
  "closing",
];

const SLIDE_TYPE_LABEL: Record<PlannedSlide["slide_type"], string> = {
  title: "Titel",
  agenda: "Agenda",
  content: "Inhalt",
  data: "Daten / Chart",
  section: "Abschnitt",
  closing: "Schluss",
};

export default function PresentationWizard({
  open,
  onClose,
  projectId,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  projectId: string;
  onCreated?: (p: PresentationOut) => void;
}) {
  const toast = useToast();

  const [step, setStep] = useState<Step>("brief");
  // Brief
  const [title, setTitle] = useState("");
  const [brief, setBrief] = useState("");
  const [audience, setAudience] = useState("");
  const [slideCount, setSlideCount] = useState(8);
  const [language, setLanguage] = useState<"de" | "en">("de");
  // Quellen-Auswahl
  const [docs, setDocs] = useState<ProjectDoc[]>([]);
  const [selectedDocIds, setSelectedDocIds] = useState<Set<string>>(new Set());
  const [docsLoading, setDocsLoading] = useState(false);
  // Direction (Genre-Preset) — null = LLM klassifiziert
  const [directions, setDirections] = useState<DirectionOption[]>([]);
  const [deckKind, setDeckKind] = useState<string | null>(null);
  // Recherche-Tiefe (off/normal/deep). Wird beim create mitgegeben und im
  // /plan-Step als Web-Recherche-Augmentation genutzt (normal->sonar,
  // deep->sonar-pro), OHNE den nativen create->plan->generate-Flow zu ersetzen.
  // off => kein Perplexity-Egress. Default normal (localStorage `sb_research_mode`).
  const [researchMode, setResearchMode] = useResearchMode();
  // After-create
  const [pres, setPres] = useState<PresentationOut | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep("brief");
    setTitle("");
    setBrief("");
    setAudience("");
    setSlideCount(8);
    setLanguage("de");
    setPres(null);
    setBusy(false);
    setDocs([]);
    setSelectedDocIds(new Set());
    setDeckKind(null);
  }, [open]);

  // Directions (6 Genre-Presets) einmalig laden — Liste ist statisch
  useEffect(() => {
    if (!open || directions.length > 0) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get<{ directions: DirectionOption[] }>(
          `/presentations/-/directions`
        );
        if (!cancelled) setDirections(r.directions || []);
      } catch {
        // Fail-soft: kein Dropdown -> Server klassifiziert automatisch.
        if (!cancelled) setDirections([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, directions.length]);

  // Projekt-Dokumente laden (für Quellen-Auswahl). Default: alle ausgewählt.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setDocsLoading(true);
    (async () => {
      try {
        const all = await api.get<ProjectDoc[]>(
          `/documents?project_id=${encodeURIComponent(projectId)}`
        );
        if (cancelled) return;
        setDocs(all);
        setSelectedDocIds(new Set(all.map((d) => d.id)));
      } catch {
        if (!cancelled) setDocs([]);
      } finally {
        if (!cancelled) setDocsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, projectId]);

  const canBrief = title.trim().length > 0 && !busy;

  const createAndPlan = async () => {
    if (!canBrief) return;
    setBusy(true);
    // EIN nativer Flow für ALLE Modi (off/normal/deep): create -> /plan ->
    // (User editiert Storyboard) -> /generate. `research_mode` wird beim create
    // mitgegeben und im Plan-Step als Web-Recherche-Augmentation genutzt
    // (normal -> sonar, deep -> sonar-pro; off -> kein Perplexity-Egress).
    // document_ids-Auswahl + Plan-Review-Step bleiben für alle Modi erhalten.
    try {
      // document_ids nur senden wenn nicht alle Docs ausgewählt sind —
      // null = alle (Server-side Default).
      const allSelected =
        docs.length > 0 && selectedDocIds.size === docs.length;
      const doc_ids =
        allSelected || selectedDocIds.size === 0
          ? null
          : [...selectedDocIds];
      const created = await api.post<PresentationOut>(
        `/projects/${projectId}/presentations`,
        {
          title: title.trim(),
          brief: brief.trim() || null,
          audience: audience.trim() || null,
          target_slide_count: slideCount,
          language,
          document_ids: doc_ids,
          deck_kind: deckKind,  // null = Server klassifiziert via LLM
          research_mode: researchMode,  // off | normal | deep
        }
      );
      // Plan-Step ist sync (bei research != off zzgl. Perplexity-Läufe).
      const planned = await api.post<PresentationOut>(
        `/presentations/${created.id}/plan`
      );
      setPres(planned);
      setStep("plan");
    } catch (e) {
      toast.err("Plan-Schritt fehlgeschlagen", (e as Error).message);
      setStep("brief");
    } finally {
      setBusy(false);
    }
  };

  const savePlanAndGenerate = async () => {
    if (!pres || !pres.plan) return;
    setBusy(true);
    setStep("generating");
    try {
      // Plan-Edits zurückschreiben (PATCH ist kompatibel)
      await api.patch(`/presentations/${pres.id}`, { plan: pres.plan });
      const done = await api.post<PresentationOut>(
        `/presentations/${pres.id}/generate`
      );
      setPres(done);
      setStep("done");
      onCreated?.(done);
    } catch (e) {
      toast.err("Generierung fehlgeschlagen", (e as Error).message);
      setStep("plan");
    } finally {
      setBusy(false);
    }
  };

  const updateSlide = (idx: number, patch: Partial<PlannedSlide>) => {
    setPres((p) => {
      if (!p || !p.plan) return p;
      const slides = p.plan.slides.map((s, i) =>
        i === idx ? { ...s, ...patch } : s
      );
      return { ...p, plan: { slides } };
    });
  };

  const moveSlide = (idx: number, dir: -1 | 1) => {
    setPres((p) => {
      if (!p || !p.plan) return p;
      const slides = [...p.plan.slides];
      const target = idx + dir;
      if (target < 0 || target >= slides.length) return p;
      [slides[idx], slides[target]] = [slides[target], slides[idx]];
      return { ...p, plan: { slides: slides.map((s, i) => ({ ...s, position: i + 1 })) } };
    });
  };

  const removeSlide = (idx: number) => {
    setPres((p) => {
      if (!p || !p.plan) return p;
      const slides = p.plan.slides.filter((_, i) => i !== idx);
      return { ...p, plan: { slides: slides.map((s, i) => ({ ...s, position: i + 1 })) } };
    });
  };

  const addSlide = () => {
    setPres((p) => {
      if (!p || !p.plan) return p;
      const newSlide: PlannedSlide = {
        position: p.plan.slides.length + 1,
        slide_type: "content",
        title: "Neue Slide",
        hint: "",
        wants_image: false,
      };
      return { ...p, plan: { slides: [...p.plan.slides, newSlide] } };
    });
  };

  const downloadPptx = async () => {
    if (!pres) return;
    const t = authToken();
    const r = await fetch(apiUrl(`/presentations/${pres.id}/pptx`), {
      headers: t ? { Authorization: `Bearer ${t}` } : {},
    });
    if (!r.ok) {
      toast.err("Download fehlgeschlagen", `HTTP ${r.status}`);
      return;
    }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${pres.title.replace(/[^a-zA-Z0-9-_ ]/g, "_")}.pptx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 animate-fade-in" />
        <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 bg-surface border border-border rounded-2xl shadow-2xl w-[760px] max-w-[94vw] max-h-[92vh] flex flex-col animate-modal-in">
          <div className="px-5 py-3 border-b border-border flex items-center gap-2">
            <PresentationIcon className="w-4 h-4 text-brand-600" />
            <Dialog.Title className="font-semibold text-sm flex-1">
              Neue Präsentation erstellen
            </Dialog.Title>
            <Dialog.Close asChild>
              <button className="p-1 text-text-3 hover:text-text">
                <X className="w-4 h-4" />
              </button>
            </Dialog.Close>
          </div>

          <Stepper step={step} />

          <div className="flex-1 overflow-y-auto px-5 py-4">
            {step === "brief" && (
              <div className="space-y-4">
                <Field label="Titel der Präsentation" required>
                  <input
                    autoFocus
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="z.B. medEctoin EIL07 — CER Q3-Update"
                    className="w-full px-3 py-2 text-sm bg-surface border border-border-strong rounded-md focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                    maxLength={200}
                  />
                </Field>
                <Field label="Brief / Ziel">
                  <textarea
                    value={brief}
                    onChange={(e) => setBrief(e.target.value)}
                    placeholder="Was soll die Präsentation vermitteln? Fokus, Erwartung des Publikums, Tonalität. Je konkreter, desto besser der Plan."
                    rows={4}
                    className="w-full px-3 py-2 text-sm bg-surface border border-border-strong rounded-md focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 resize-none"
                  />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Zielgruppe">
                    <input
                      type="text"
                      value={audience}
                      onChange={(e) => setAudience(e.target.value)}
                      placeholder="z.B. Aufsichtsrat / Fachpublikum"
                      className="w-full px-3 py-2 text-sm bg-surface border border-border-strong rounded-md focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                    />
                  </Field>
                  <Field label="Anzahl Slides">
                    <input
                      type="number"
                      min={3}
                      max={30}
                      value={slideCount}
                      onChange={(e) => setSlideCount(Math.max(3, Math.min(30, Number(e.target.value) || 8)))}
                      className="w-full px-3 py-2 text-sm bg-surface border border-border-strong rounded-md focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                    />
                  </Field>
                </div>
                <Field label="Sprache">
                  <div className="flex gap-2">
                    {(["de", "en"] as const).map((lng) => (
                      <button
                        key={lng}
                        type="button"
                        onClick={() => setLanguage(lng)}
                        className={clsx(
                          "px-3 py-1.5 text-sm rounded-md border transition-colors duration-150",
                          language === lng
                            ? "bg-brand-600 text-white border-brand-600"
                            : "bg-surface border-border-strong text-text-2 hover:border-brand-400"
                        )}
                      >
                        {lng === "de" ? "Deutsch" : "English"}
                      </button>
                    ))}
                  </div>
                </Field>

                <ResearchModePicker
                  mode={researchMode}
                  setMode={setResearchMode}
                  variant="full"
                />

                {directions.length > 0 && (
                  <Field label="Stilrichtung">
                    <select
                      value={deckKind ?? ""}
                      onChange={(e) =>
                        setDeckKind(e.target.value === "" ? null : e.target.value)
                      }
                      className="w-full px-3 py-2 text-sm bg-surface border border-border-strong rounded-md focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                    >
                      <option value="">
                        🤖 LLM klassifiziert aus Brief (Empfehlung)
                      </option>
                      {directions.map((d) => (
                        <option key={d.key} value={d.key}>
                          {d.label}
                        </option>
                      ))}
                    </select>
                    {deckKind && (
                      <div className="mt-1.5 text-[11px] text-text-2 leading-relaxed">
                        {directions.find((d) => d.key === deckKind)?.description}
                        <div className="mt-1 text-text-3">
                          <span className="font-medium">Use-Cases:</span>{" "}
                          {directions
                            .find((d) => d.key === deckKind)
                            ?.use_cases.join(" · ")}
                        </div>
                      </div>
                    )}
                    {!deckKind && (
                      <div className="mt-1.5 text-[11px] text-text-2 italic">
                        Der Brief entscheidet automatisch — z.B. „Investor-Pitch"
                        oder „Aufsichtsrat-Status" wählen die passende Stilrichtung.
                      </div>
                    )}
                  </Field>
                )}

                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs font-medium text-text inline-flex items-center gap-1">
                      <FileText className="w-3 h-3" /> Quellen ({selectedDocIds.size}/{docs.length})
                    </label>
                    <div className="flex items-center gap-1 text-[11px]">
                      <button
                        type="button"
                        onClick={() => setSelectedDocIds(new Set(docs.map((d) => d.id)))}
                        className="text-brand-600 hover:underline"
                      >
                        Alle
                      </button>
                      <span className="text-text-3">·</span>
                      <button
                        type="button"
                        onClick={() => setSelectedDocIds(new Set())}
                        className="text-text-2 hover:text-text"
                      >
                        Keine
                      </button>
                    </div>
                  </div>
                  {docsLoading ? (
                    <div className="text-xs text-text-2 italic flex items-center gap-1.5 py-2">
                      <Loader2 className="w-3 h-3 animate-spin" /> Dokumente laden …
                    </div>
                  ) : docs.length === 0 ? (
                    <div className="text-xs text-text-2 italic py-2">
                      Keine Dokumente im Projekt — die Präsentation wird ohne RAG-Quellen generiert.
                    </div>
                  ) : (
                    <ul className="border border-border rounded-md divide-y divide-border max-h-[220px] overflow-y-auto">
                      {docs.map((d) => {
                        const checked = selectedDocIds.has(d.id);
                        return (
                          <li key={d.id}>
                            <label className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-surface-2">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => {
                                  setSelectedDocIds((prev) => {
                                    const next = new Set(prev);
                                    if (next.has(d.id)) next.delete(d.id);
                                    else next.add(d.id);
                                    return next;
                                  });
                                }}
                                className="accent-brand-600 w-3.5 h-3.5"
                              />
                              <FileText className="w-3 h-3 text-text-3" />
                              <span className="text-sm flex-1 truncate">{d.title}</span>
                              {d.brain_store_doc_id && (
                                <span className="text-xs uppercase tracking-wider text-brand-600 dark:text-brand-400">
                                  Mode-B
                                </span>
                              )}
                              <span className="text-[10px] text-text-3">
                                {d.chunk_count} Blöcke
                              </span>
                            </label>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>
            )}

            {step === "plan" && pres?.plan && (
              <div className="space-y-3">
                <div className="text-sm text-text">
                  Hier ist der vorgeschlagene Slide-Plan. Du kannst Titel
                  ändern, Slides umsortieren, löschen oder neue hinzufügen.
                </div>
                {pres.plan.deck_kind && (
                  <div className="text-[11px] px-2.5 py-1.5 rounded-md bg-brand-50 dark:bg-brand-950/30 border border-brand-200 dark:border-brand-800 text-brand-800 dark:text-brand-200 flex items-start gap-1.5">
                    <span className="font-medium shrink-0">Stilrichtung:</span>
                    <span>
                      {directions.find((d) => d.key === pres.plan!.deck_kind)
                        ?.label ?? pres.plan.deck_kind}
                      {pres.plan.direction_classifier && (
                        <span className="text-text-2 ml-1">
                          {pres.plan.direction_classifier.fallback
                            ? "(Fallback — LLM nicht erreichbar)"
                            : `(LLM-klassifiziert, conf=${(
                                pres.plan.direction_classifier.confidence ?? 0
                              ).toFixed(2)})`}
                          {pres.plan.direction_classifier.rationale && (
                            <span className="block italic mt-0.5">
                              {pres.plan.direction_classifier.rationale}
                            </span>
                          )}
                        </span>
                      )}
                    </span>
                  </div>
                )}
                <div className="space-y-2">
                  {pres.plan.slides.map((s, i) => (
                    <div
                      key={i}
                      className="border border-border rounded-md p-2.5 bg-surface"
                    >
                      <div className="flex items-center gap-2">
                        <div className="flex flex-col gap-0.5">
                          <button
                            onClick={() => moveSlide(i, -1)}
                            disabled={i === 0}
                            className="text-text-3 hover:text-brand-600 disabled:opacity-20 text-[10px]"
                          >
                            ▲
                          </button>
                          <button
                            onClick={() => moveSlide(i, 1)}
                            disabled={i === pres.plan!.slides.length - 1}
                            className="text-text-3 hover:text-brand-600 disabled:opacity-20 text-[10px]"
                          >
                            ▼
                          </button>
                        </div>
                        <span className="text-xs text-text-3 w-6 text-right tabular-nums">
                          {i + 1}.
                        </span>
                        <select
                          value={s.slide_type}
                          onChange={(e) =>
                            updateSlide(i, {
                              slide_type: e.target.value as PlannedSlide["slide_type"],
                            })
                          }
                          className="text-xs px-1.5 py-1 bg-surface border border-border rounded-md"
                        >
                          {SLIDE_TYPES.map((t) => (
                            <option key={t} value={t}>
                              {SLIDE_TYPE_LABEL[t]}
                            </option>
                          ))}
                        </select>
                        <input
                          type="text"
                          value={s.title}
                          onChange={(e) => updateSlide(i, { title: e.target.value })}
                          placeholder="Slide-Titel"
                          className="flex-1 text-sm px-2 py-1 bg-transparent border border-transparent hover:border-border focus:border-brand-500 focus:ring-1 focus:ring-brand-500 rounded-md outline-none"
                        />
                        <label className="text-[11px] inline-flex items-center gap-1 text-text-2">
                          <input
                            type="checkbox"
                            checked={s.wants_image}
                            onChange={(e) =>
                              updateSlide(i, { wants_image: e.target.checked })
                            }
                            className="accent-brand-600 w-3.5 h-3.5"
                          />
                          <ImageIcon className="w-3 h-3" /> Bild
                        </label>
                        <button
                          onClick={() => removeSlide(i)}
                          className="text-text-3 hover:text-danger p-1"
                          title="Slide entfernen"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <input
                        type="text"
                        value={s.hint}
                        onChange={(e) => updateSlide(i, { hint: e.target.value })}
                        placeholder="Hinweis was auf die Slide soll (1 Satz)"
                        className="mt-1 w-full text-[12px] px-2 py-1 bg-surface-2 border border-border rounded-md text-text-2 focus:outline-none focus:border-brand-400"
                      />
                    </div>
                  ))}
                </div>
                <button
                  onClick={addSlide}
                  className="w-full text-sm border border-dashed border-border-strong rounded-md py-2 text-text-2 hover:text-brand-600 hover:border-brand-400 inline-flex items-center justify-center gap-1.5 transition-colors duration-150"
                >
                  <Plus className="w-3.5 h-3.5" /> Slide hinzufügen
                </button>
              </div>
            )}

            {step === "generating" && (
              <div className="py-14 flex flex-col items-center text-center space-y-3">
                <Loader2 className="w-10 h-10 text-brand-500 animate-spin" />
                <div className="text-sm font-medium">Generiere Slides …</div>
                <div className="text-xs text-text-2 max-w-xs">
                  Pro Slide: hybrid Retrieval, Gemini-Synthese, Bild-Routing.
                  Erfahrungswert: 15–45 s bei 8 Slides.
                </div>
              </div>
            )}

            {step === "done" && pres && (
              <div className="py-8 flex flex-col items-center text-center space-y-3">
                {pres.status === "done" ? (
                  <>
                    <CheckCircle2 className="w-12 h-12 text-success" />
                    <div className="text-sm font-medium">
                      Präsentation „{pres.title}" ist fertig.
                    </div>
                    <div className="text-xs text-text-2">
                      {pres.plan?.slides.length} Slides · {pres.tokens_in + pres.tokens_out} Token
                    </div>
                    <Button
                      variant="primary"
                      size="lg"
                      onClick={downloadPptx}
                      className="mt-2"
                      icon={<Download className="w-4 h-4" />}
                    >
                      .pptx herunterladen
                    </Button>
                  </>
                ) : (
                  <>
                    <AlertCircle className="w-12 h-12 text-danger" />
                    <div className="text-sm font-medium">Generierung fehlgeschlagen</div>
                    <div className="text-xs text-danger max-w-md">
                      {pres.error || "(keine Detail-Fehlermeldung)"}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          <div className="px-5 py-3 border-t border-border flex items-center justify-between gap-2">
            <div>
              {step === "plan" && (
                <Button
                  variant="ghost"
                  size="md"
                  onClick={() => setStep("brief")}
                  icon={<ArrowLeft className="w-3.5 h-3.5" />}
                >
                  Zurück
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              {step === "brief" && (
                <>
                  <Button variant="ghost" size="md" onClick={onClose}>
                    Abbrechen
                  </Button>
                  <Button
                    variant="primary"
                    size="md"
                    onClick={createAndPlan}
                    disabled={!canBrief}
                    loading={busy}
                  >
                    Plan erstellen <ArrowRight className="w-3.5 h-3.5" />
                  </Button>
                </>
              )}
              {step === "plan" && (
                <Button
                  variant="primary"
                  size="md"
                  onClick={savePlanAndGenerate}
                  disabled={busy || !pres?.plan?.slides.length}
                >
                  Generieren <ArrowRight className="w-3.5 h-3.5" />
                </Button>
              )}
              {step === "done" && (
                <Button variant="primary" size="md" onClick={onClose}>
                  Schließen
                </Button>
              )}
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function Stepper({ step }: { step: Step }) {
  const stages: { id: Step; label: string }[] = [
    { id: "brief", label: "Brief" },
    { id: "plan", label: "Plan" },
    { id: "generating", label: "Generieren" },
    { id: "done", label: "Fertig" },
  ];
  const idx = stages.findIndex((s) => s.id === step);
  return (
    <div className="px-5 py-2 border-b border-border flex items-center gap-1.5">
      {stages.map((s, i) => (
        <div key={s.id} className="flex items-center gap-1.5">
          <div
            className={clsx(
              "w-5 h-5 rounded-full grid place-items-center text-[10px] font-medium",
              i < idx
                ? "bg-success text-white"
                : i === idx
                  ? "bg-brand-600 text-white"
                  : "bg-surface-3 text-text-2"
            )}
          >
            {i + 1}
          </div>
          <span
            className={clsx(
              "text-[11px]",
              i === idx
                ? "text-text dark:text-white font-medium"
                : "text-text-2"
            )}
          >
            {s.label}
          </span>
          {i < stages.length - 1 && (
            <div className="w-4 h-px bg-surface-3 mx-0.5" />
          )}
        </div>
      ))}
    </div>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-xs font-medium text-text mb-1.5 block">
        {label}
        {required && <span className="text-danger ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}
